import { FloatingFormItem, InputDate } from '@/components/_common';
import {
  InvoiceGeneralFormValues,
  OperationDestination,
} from '@/core/domain/invoice/invoice/Invoice.types';
import {
  InvoiceDocumentType,
  InvoiceEmissionGoal,
  InvoiceEmissionType,
  InvoiceType,
} from '@/core/domain/invoice/invoices/invoices.enums';
import {
  useDefaultEmptyFieldError,
  useDefaultMaxLengthFieldError,
  useDefaultMinLengthFieldError,
} from '@/hooks';
import { Col, Form, Input, Row, Select } from 'antd';
import React, { useCallback, useEffect } from 'react';
import { taxationNatureOptions } from './constants';
import { useQueryClient } from 'react-query';
import { InvoiceSettingsData } from '@/core/domain/invoice/settings/Settings.types';
import { useIntl } from 'react-intl';
import InvoiceStepAdvancedData from './AdvancedData';
import moment from 'moment';
import InvoiceAddressFields from '../../../InvoiceCreateModal/InvoiceAddressFields';
import EmissionGoalSelect from '@/components/_common/EmissionGoalSelect';
import SelectYesNo from '@/components/_common/SelectYesNo';

const InvoiceStepConfiguration: React.FC = () => {
  const intl = useIntl().formatMessage;
  const form = Form.useFormInstance<InvoiceGeneralFormValues>();
  const invoiceType = Form.useWatch('invoiceType', form);
  const companyId = Form.useWatch('companyId', form);

  const ruleRequired = useDefaultEmptyFieldError();
  const defaultMaxLenghtFieldError = useDefaultMaxLengthFieldError();
  const defaultMinLenghtFieldError = useDefaultMinLengthFieldError();

  const queryClient = useQueryClient();
  const settings = queryClient.getQueryData<InvoiceSettingsData | undefined>([
    'invoiceSettings/company',
    companyId,
  ]);

  useEffect(() => {
    if (invoiceType !== InvoiceType.NFS || !settings?.configNfs) {
      return;
    }

    const {
      issqnServiceCodeDefault,
      operationIndicatorCodeDefault,
      taxationNatureDefault,
      operationTypeDefault,
    } = settings.configNfs;

    form.setFieldsValue({
      operationIndicatorCode: operationIndicatorCodeDefault,
      taxationNature: taxationNatureDefault,
      operationType: operationTypeDefault,
    });

    if (issqnServiceCodeDefault) {
      form.setFieldValue('serviceItemList', issqnServiceCodeDefault);
    }
  }, [invoiceType, settings?.configNfs]);

  const getSeriesOptions = useCallback(() => {
    let options = [] as {
      value: string;
      label: string;
    }[];

    if (invoiceType === 'NFE') {
      options =
        settings?.configNfe?.invoiceNumberings?.map((item) => ({
          value: item.series,
          label: item.series,
        })) || [];
    } else if (invoiceType === 'NFC') {
      options =
        settings?.configNfc?.invoiceNumberings?.map((item) => ({
          value: item.series,
          label: item.series,
        })) || [];
    } else if (invoiceType === 'NFS') {
      options =
        settings?.configNfs?.invoiceNumberings?.map((item) => ({
          value: item.series,
          label: item.series,
        })) || [];
    }

    if (options.length >= 1) {
      form.setFieldValue('serie', options[0].value);
    }

    return options;
  }, [settings, invoiceType]);

  const emissionType = Form.useWatch(['emissionType'], form);

  return (
    <>
      <Form.Item name="situation" hidden />
      <Form.Item name="integrationId" hidden />

      <p>
        <strong>Dados de configuração da nota</strong>
      </p>

      <Row gutter={[16, 16]}>
        <Col span={24}>
          <InvoiceAddressFields companyId={companyId} form={form} />
        </Col>

        <Col span={24} md={12} lg={6}>
          <FloatingFormItem
            label={intl({
              id: 'invoice.domain.series',
            })}
            name="serie"
            rules={[ruleRequired]}
          >
            <Select options={getSeriesOptions()} />
          </FloatingFormItem>
        </Col>

        {invoiceType === InvoiceType.NFS && (
          <>
            <Col span={24} md={12} lg={6}>
              <FloatingFormItem
                key={invoiceType}
                label="Natureza da tributação"
                name="taxationNature"
                rules={[ruleRequired]}
              >
                <Select options={taxationNatureOptions} />
              </FloatingFormItem>
            </Col>

            {!settings?.configNfs?.automaticNumbering && (
              <Col span={24} md={12} lg={6}>
                <FloatingFormItem
                  noStyle
                  label={intl({ id: 'invoice.nfse.domain.numberRps' })}
                  name="numberRps"
                >
                  <Input />
                </FloatingFormItem>
              </Col>
            )}

            <Col xs={24} md={12} xl={6}>
              <FloatingFormItem
                label="Código serviços"
                name="serviceItemList"
                rules={[ruleRequired]}
              >
                <Input />
              </FloatingFormItem>
            </Col>
          </>
        )}

        {invoiceType !== InvoiceType.NFS && (
          <>
            <Col span={24} md={12} lg={6}>
              <FloatingFormItem
                key={invoiceType}
                label="Natureza da operação"
                name="natureOfOperation"
                rules={[ruleRequired]}
              >
                <Input />
              </FloatingFormItem>
            </Col>

            {invoiceType === InvoiceType.NFE && (
              <Col span={24} md={12} lg={6}>
                <FloatingFormItem
                  label={intl({
                    id: 'invoice.domain.emissionGoals',
                  })}
                  name="emissionGoal"
                  rules={[ruleRequired]}
                  initialValue={InvoiceEmissionGoal.NFE_NORMAL}
                >
                  <EmissionGoalSelect allowClear />
                </FloatingFormItem>
              </Col>
            )}

            <Col span={24} md={12} lg={6}>
              <FloatingFormItem
                label={intl({ id: 'invoice.domain.documentType' })}
                name="documentType"
                rules={[ruleRequired]}
                initialValue={InvoiceDocumentType.EXIT}
              >
                <Select
                  allowClear
                  style={{ width: '100%' }}
                  options={[
                    {
                      value: InvoiceDocumentType.ENTRY,
                      label: intl({ id: 'invoice.domain.documentType.entry' }),
                    },
                    {
                      value: InvoiceDocumentType.EXIT,
                      label: intl({ id: 'invoice.domain.documentType.exit' }),
                    },
                  ]}
                />
              </FloatingFormItem>
            </Col>

            <Col span={24} md={12} lg={6}>
              <FloatingFormItem
                label={intl({ id: 'invoice.domain.operationDestination' })}
                name="operationDestination"
                rules={[ruleRequired]}
              >
                <Select
                  allowClear
                  style={{ width: '100%' }}
                  options={[
                    {
                      value: OperationDestination.internalOperation,
                      label: intl({
                        id: 'invoice.domain.operationDestination.internalOperation',
                      }),
                    },
                    {
                      value: OperationDestination.interstateOperation,
                      label: intl({
                        id: 'invoice.domain.operationDestination.interstateOperation',
                      }),
                    },
                    {
                      value: OperationDestination.foreignOperation,
                      label: intl({
                        id: 'invoice.domain.operationDestination.foreignOperation',
                      }),
                    },
                  ]}
                />
              </FloatingFormItem>
            </Col>

            <Col span={24} md={12} lg={6}>
              <FloatingFormItem
                label={intl({ id: 'invoice.domain.entryExitDate' })}
                name="departureDate"
                initialValue={moment()}
              >
                <InputDate showTime />
              </FloatingFormItem>
            </Col>

            <Col span={24} md={12} lg={6}>
              <FloatingFormItem
                label={intl({ id: 'invoice.domain.emissionType' })}
                name="emissionType"
                rules={[ruleRequired]}
                initialValue={InvoiceEmissionType.NORMAL_EMISSION}
              >
                <Select
                  allowClear
                  style={{ width: '100%' }}
                  options={[
                    {
                      value: InvoiceEmissionType.NORMAL_EMISSION,
                      label: intl({ id: 'invoice.domain.emissionType.normal' }),
                    },
                    {
                      value: InvoiceEmissionType.CONTINGENCY_FS_IA,
                      label: intl({
                        id: 'invoice.domain.emissionType.contingencyFs',
                      }),
                    },
                    {
                      value: InvoiceEmissionType.CONTINGENCY_DPEC,
                      label: intl({
                        id: 'invoice.domain.emissionType.contingencyDpec',
                      }),
                    },
                    {
                      value: InvoiceEmissionType.CONTINGENCY_FS_DA,
                      label: intl({
                        id: 'invoice.domain.emissionType.contingencyFsda',
                      }),
                    },
                    {
                      value: InvoiceEmissionType.CONTINGENCY_SVC_AN,
                      label: intl({
                        id: 'invoice.domain.emissionType.contingencySvcan',
                      }),
                    },
                    {
                      value: InvoiceEmissionType.CONTINGENCY_SVC_RS,
                      label: intl({
                        id: 'invoice.domain.emissionType.contingencySvcrs',
                      }),
                    },
                  ]}
                />
              </FloatingFormItem>
            </Col>

            {(emissionType === InvoiceEmissionType.CONTINGENCY_SVC_AN ||
              emissionType === InvoiceEmissionType.CONTINGENCY_SVC_RS) && (
              <Col span={24} md={12} lg={6}>
                <FloatingFormItem
                  label="Justificativa de contingência"
                  name="contingencyJustification"
                  rules={[
                    ruleRequired,
                    defaultMinLenghtFieldError(15),
                    defaultMaxLenghtFieldError(255),
                  ]}
                >
                  <Input />
                </FloatingFormItem>
              </Col>
            )}

            <Col span={24} md={12} lg={6}>
              <FloatingFormItem
                label="Operação presencial"
                name="faceToFace"
                rules={[ruleRequired]}
                initialValue={'true'}
              >
                <SelectYesNo allowClear />
              </FloatingFormItem>
            </Col>
          </>
        )}

        <Col span={24}>
          <InvoiceStepAdvancedData invoiceType={invoiceType} />
        </Col>
      </Row>
    </>
  );
};

export default InvoiceStepConfiguration;
